import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Edit2, Trash2, Tag, Printer, QrCode, X, Eye } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import LabelCanvas from '../components/LabelCanvas';

const LabelsPage = () => {
  const { api } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [finishedUnits, setFinishedUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [activeTab, setActiveTab] = useState('templates');
  const printRef = useRef(null);
  
  const [formData, setFormData] = useState({
    template_name: '',
    label_type: 'Item',
    width_mm: 100,
    height_mm: 50,
    fields: [],
  });

  const [printConfig, setPrintConfig] = useState({
    label_type: 'Item',
    entity_id: '',
  });

  const [newField, setNewField] = useState({
    field_name: '',
    field_type: 'text',
    data_source: '',
    data_path: '',
    static_value: '',
    x_position: 0,
    y_position: 0,
    width: 100,
    height: 30,
    font_size: 12,
  });

  const [fieldSources, setFieldSources] = useState({ sources: {}, label_type_defaults: {}, label_types: [] });

  const fetchData = async () => {
    try {
      const [templatesRes, itemsRes, locRes, woRes, fuRes, fsRes] = await Promise.all([
        api().get('/label-templates'),
        api().get('/items'),
        api().get('/locations'),
        api().get('/work-orders'),
        api().get('/finished-units'),
        api().get('/label-templates/field-sources'),
      ]);
      setTemplates(templatesRes.data);
      setItems(itemsRes.data);
      setLocations(locRes.data);
      setWorkOrders(woRes.data);
      setFinishedUnits(fuRes.data);
      setFieldSources(fsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        width_mm: parseInt(formData.width_mm),
        height_mm: parseInt(formData.height_mm),
      };
      if (editingItem) {
        await api().put(`/label-templates/${editingItem.id}`, payload);
        toast.success('Template updated');
      } else {
        await api().post('/label-templates', payload);
        toast.success('Template created');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      template_name: item.template_name,
      label_type: item.label_type,
      width_mm: item.width_mm,
      height_mm: item.height_mm,
      fields: item.fields || [],
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await api().delete(`/label-templates/${id}`);
      toast.success('Template deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  // iter37 — Template preview dialog (read-only WYSIWYG)
  const [previewTpl, setPreviewTpl] = useState(null);

  const addField = () => {
    if (!newField.field_name) {
      toast.error('Field name is required');
      return;
    }
    if (newField.field_type === 'linked' && (!newField.data_source || !newField.data_path)) {
      toast.error('Pick a source and path for linked fields');
      return;
    }
    setFormData({
      ...formData,
      fields: [...formData.fields, { ...newField, id: Date.now().toString() }],
    });
    setNewField({
      field_name: '',
      field_type: 'text',
      data_source: '',
      data_path: '',
      static_value: '',
      x_position: 0,
      y_position: 0,
      width: 100,
      height: 30,
      font_size: 12,
    });
  };

  const removeField = (fieldId) => {
    setFormData({
      ...formData,
      fields: formData.fields.filter(f => f.id !== fieldId),
    });
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      template_name: '',
      label_type: 'Item',
      width_mm: 100,
      height_mm: 50,
      fields: [],
    });
  };

  const getEntityOptions = () => {
    switch (printConfig.label_type) {
      case 'Item':
        return items.map(i => ({ id: i.item_sku, label: `${i.item_sku} - ${i.item_name}` }));
      case 'Location':
        return locations.map(l => ({ id: l.id, label: `${l.location_name} - ${l.bin_id}` }));
      case 'WorkOrder':
        return workOrders.map(wo => ({ id: wo.id, label: `WO-${wo.id.slice(0, 8)}` }));
      case 'FinishedUnit':
        return finishedUnits.map(fu => ({ id: fu.serial_number, label: fu.serial_number }));
      default:
        return [];
    }
  };

  const getLabelContent = () => {
    const entity = printConfig.entity_id;
    switch (printConfig.label_type) {
      case 'Item':
        const item = items.find(i => i.item_sku === entity);
        return item ? {
          title: item.item_name,
          subtitle: item.item_sku,
          qrValue: item.item_sku,
          details: [`Category: ${item.item_category}`, `UOM: ${item.base_uom}`]
        } : null;
      case 'Location':
        const loc = locations.find(l => l.id === entity);
        return loc ? {
          title: loc.location_name,
          subtitle: loc.bin_id,
          qrValue: `location:${loc.id}`,
          details: [`Type: ${loc.bin_type}`]
        } : null;
      case 'WorkOrder':
        const wo = workOrders.find(w => w.id === entity);
        return wo ? {
          title: `Work Order`,
          subtitle: wo.id.slice(0, 8),
          qrValue: `wo:${wo.id}`,
          details: [`Target: ${wo.target_quantity}`, `Status: ${wo.status}`]
        } : null;
      case 'FinishedUnit':
        const fu = finishedUnits.find(f => f.serial_number === entity);
        return fu ? {
          title: 'Finished Unit',
          subtitle: fu.serial_number,
          qrValue: `unit:${fu.serial_number}`,
          details: [`Built: ${new Date(fu.build_date).toLocaleDateString()}`]
        } : null;
      default:
        return null;
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Label</title>
          <style>
            body { font-family: 'IBM Plex Sans', sans-serif; margin: 0; padding: 20px; }
            .label { border: 1px solid #e2e8f0; padding: 16px; display: inline-block; }
            .title { font-weight: bold; font-size: 16px; margin-bottom: 4px; }
            .subtitle { font-family: 'JetBrains Mono', monospace; font-size: 14px; margin-bottom: 8px; }
            .details { font-size: 12px; color: #64748b; }
            .qr { margin-top: 8px; }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
          <script>window.print(); window.close();</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const labelContent = getLabelContent();

  return (
    <div className="space-y-6 animate-slideIn" data-testid="labels-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Labels & QR Codes</h1>
          <p className="text-slate-500 text-sm">Configure label templates and print labels</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="templates" data-testid="labels-templates-tab">Templates</TabsTrigger>
          <TabsTrigger value="print" data-testid="labels-print-tab">Print Labels</TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="add-template-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Template
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingItem ? 'Edit Template' : 'Create Template'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Template Name *</Label>
                    <Input
                      value={formData.template_name}
                      onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
                      placeholder="e.g., Standard Item Label"
                      required
                      className="rounded-sm"
                      data-testid="template-name-input"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Label Type *</Label>
                      <Select
                        value={formData.label_type}
                        onValueChange={(v) => {
                          // when label type changes, suggest a sensible default source
                          const def = fieldSources.label_type_defaults?.[v] || '';
                          setFormData({ ...formData, label_type: v });
                          setNewField((f) => ({ ...f, data_source: def, data_path: '' }));
                        }}
                      >
                        <SelectTrigger className="rounded-sm" data-testid="template-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(fieldSources.label_types && fieldSources.label_types.length
                            ? fieldSources.label_types
                            : [
                                { value: 'Item', label: 'Item' },
                                { value: 'Location', label: 'Location' },
                                { value: 'Lot', label: 'Lot / Batch' },
                                { value: 'WorkOrder', label: 'Work Order' },
                                { value: 'ProductionQueue', label: 'Production Queue' },
                                { value: 'FinishedUnit', label: 'Finished Unit' },
                                { value: 'BOM', label: 'BOM' },
                                { value: 'Category', label: 'Category' },
                              ]
                          ).map((lt) => (
                            <SelectItem key={lt.value} value={lt.value}>{lt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Width (mm)</Label>
                      <Input
                        type="number"
                        value={formData.width_mm}
                        onChange={(e) => setFormData({ ...formData, width_mm: e.target.value })}
                        className="rounded-sm mono"
                        data-testid="template-width-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Height (mm)</Label>
                      <Input
                        type="number"
                        value={formData.height_mm}
                        onChange={(e) => setFormData({ ...formData, height_mm: e.target.value })}
                        className="rounded-sm mono"
                        data-testid="template-height-input"
                      />
                    </div>
                  </div>

                  {/* Fields Section */}
                  <div className="border border-slate-200 rounded-sm p-4 space-y-4">
                    <Label className="text-base font-semibold">Label Fields</Label>
                    <p className="text-xs text-slate-500">
                      Build the label by adding fields. Choose <span className="font-medium">Linked</span> to pull a value from the entity at print time
                      (e.g., <span className="mono">Item.SKU</span>, <span className="mono">BOM.Variant Code</span>).
                    </p>
                    <div className="grid grid-cols-1 gap-2 bg-slate-50 p-3 rounded-sm">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Field Name</Label>
                          <Input
                            value={newField.field_name}
                            onChange={(e) => setNewField({ ...newField, field_name: e.target.value })}
                            placeholder="e.g., SKU"
                            className="rounded-sm"
                            data-testid="new-field-name-input"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Type</Label>
                          <Select
                            value={newField.field_type}
                            onValueChange={(v) => setNewField({ ...newField, field_type: v, data_source: v === 'linked' ? (fieldSources.label_type_defaults?.[formData.label_type] || '') : '', data_path: '' })}
                          >
                            <SelectTrigger className="rounded-sm" data-testid="new-field-type-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="text" data-testid="field-type-option-text">Text (static)</SelectItem>
                              <SelectItem value="linked" data-testid="field-type-option-linked">Linked (from entity)</SelectItem>
                              <SelectItem value="qr" data-testid="field-type-option-qr">QR Code</SelectItem>
                              <SelectItem value="barcode" data-testid="field-type-option-barcode">Barcode</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      {newField.field_type === 'text' && (
                        <div>
                          <Label className="text-xs">Static Value</Label>
                          <Input
                            value={newField.static_value}
                            onChange={(e) => setNewField({ ...newField, static_value: e.target.value })}
                            placeholder="e.g., Lot:"
                            className="rounded-sm"
                            data-testid="new-field-static-input"
                          />
                        </div>
                      )}
                      {newField.field_type === 'linked' && (
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">Source</Label>
                            <Select
                              value={newField.data_source || ''}
                              onValueChange={(v) => setNewField({ ...newField, data_source: v, data_path: '' })}
                            >
                              <SelectTrigger className="rounded-sm" data-testid="new-field-source-select">
                                <SelectValue placeholder="Pick source" />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(fieldSources.sources || {}).map(([key, src]) => (
                                  <SelectItem key={key} value={key} data-testid={`field-source-option-${key}`}>
                                    {src.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Path</Label>
                            <Select
                              value={newField.data_path || ''}
                              onValueChange={(v) => setNewField({ ...newField, data_path: v })}
                            >
                              <SelectTrigger className="rounded-sm" data-testid="new-field-path-select" disabled={!newField.data_source}>
                                <SelectValue placeholder={newField.data_source ? 'Pick attribute' : 'Pick source first'} />
                              </SelectTrigger>
                              <SelectContent>
                                {(fieldSources.sources?.[newField.data_source]?.fields || []).map((f) => (
                                  <SelectItem key={f.path} value={f.path} data-testid={`field-path-option-${f.path}`}>
                                    {f.label} <span className="text-xs text-slate-400 ml-1 mono">{f.path}</span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      )}
                      {newField.field_type === 'qr' && (
                        <div>
                          <Label className="text-xs">QR Source (optional)</Label>
                          <Select
                            value={newField.data_source || ''}
                            onValueChange={(v) => setNewField({ ...newField, data_source: v, data_path: '' })}
                          >
                            <SelectTrigger className="rounded-sm" data-testid="new-field-qr-source-select">
                              <SelectValue placeholder="Default: entity URL / SKU" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="">Default (entity URL or SKU)</SelectItem>
                              {Object.entries(fieldSources.sources || {}).map(([key, src]) => (
                                <SelectItem key={key} value={key}>{src.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <Button type="button" onClick={addField} className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="add-field-btn">
                        <Plus className="w-4 h-4 mr-1" /> Add field
                      </Button>
                    </div>
                    {formData.fields.length > 0 && (
                      <div className="space-y-2">
                        {formData.fields.map((field, idx) => (
                          <div key={field.id || idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-sm" data-testid={`template-field-${field.field_name}`}>
                            <div className="flex-1 min-w-0">
                              <span className="font-medium">{field.field_name}</span>
                              <span className="ml-2 text-xs text-slate-400 mono">{field.field_type}</span>
                              {field.field_type === 'linked' && field.data_source && (
                                <span className="ml-2 text-xs text-orange-600 mono">
                                  {field.data_source}.{field.data_path}
                                </span>
                              )}
                              {field.field_type === 'text' && field.static_value && (
                                <span className="ml-2 text-xs text-slate-600 italic">"{field.static_value}"</span>
                              )}
                              {(field.x_position || field.y_position) ? (
                                <span className="ml-2 text-xs text-slate-400 mono">@ {field.x_position}mm,{field.y_position}mm</span>
                              ) : null}
                            </div>
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeField(field.id)} className="text-red-600">
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* WYSIWYG canvas — drag fields to position, resize via handle */}
                  {formData.fields.length > 0 && (
                    <div className="border border-slate-200 rounded-sm p-4 space-y-3" data-testid="template-canvas-wrap">
                      <Label className="text-base font-semibold">Visual Layout</Label>
                      <LabelCanvas
                        template={formData}
                        onChange={(nextFields) => setFormData({ ...formData, fields: nextFields })}
                      />
                    </div>
                  )}

                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="template-submit-btn">
                      {editingItem ? 'Update' : 'Create'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card className="industrial-card">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-48">
                  <div className="spinner" />
                </div>
              ) : templates.length > 0 ? (
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Template Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead>Fields</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {templates.map((item) => (
                      <TableRow key={item.id} data-testid={`template-row-${item.id}`}>
                        <TableCell className="font-medium">{item.template_name}</TableCell>
                        <TableCell>
                          <span className="status-badge status-draft">{item.label_type}</span>
                        </TableCell>
                        <TableCell className="mono">{item.width_mm}x{item.height_mm}mm</TableCell>
                        <TableCell className="mono">{item.fields?.length || 0}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => setPreviewTpl(item)} data-testid={`template-preview-${item.id}`} title="Preview">
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(item)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="flex flex-col items-center justify-center h-48 text-slate-500">
                  <Tag className="w-12 h-12 mb-3 text-slate-300" />
                  <p>No templates found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="print" className="space-y-4">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="industrial-card">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-lg">Print Configuration</CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div className="space-y-2">
                  <Label>Label Type</Label>
                  <Select value={printConfig.label_type} onValueChange={(v) => setPrintConfig({ ...printConfig, label_type: v, entity_id: '' })}>
                    <SelectTrigger className="rounded-sm" data-testid="print-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Item">Item Label</SelectItem>
                      <SelectItem value="Location">Location Label</SelectItem>
                      <SelectItem value="WorkOrder">Work Order Label</SelectItem>
                      <SelectItem value="FinishedUnit">Finished Unit Label</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Select Entity</Label>
                  <Select value={printConfig.entity_id} onValueChange={(v) => setPrintConfig({ ...printConfig, entity_id: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="print-entity-select">
                      <SelectValue placeholder="Select an entity to print" />
                    </SelectTrigger>
                    <SelectContent>
                      {getEntityOptions().map(opt => (
                        <SelectItem key={opt.id} value={opt.id}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  className="w-full bg-slate-900 hover:bg-slate-800 rounded-sm"
                  onClick={handlePrint}
                  disabled={!labelContent}
                  data-testid="print-label-btn"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print Label
                </Button>
              </CardContent>
            </Card>

            <Card className="industrial-card">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-lg">Label Preview</CardTitle>
              </CardHeader>
              <CardContent className="p-4 flex items-center justify-center min-h-[250px]">
                {labelContent ? (
                  <div ref={printRef} className="label bg-white border border-slate-200 p-4 rounded-sm inline-block">
                    <div className="title text-lg font-bold">{labelContent.title}</div>
                    <div className="subtitle mono text-sm text-slate-700">{labelContent.subtitle}</div>
                    <div className="details space-y-1 mt-2">
                      {labelContent.details.map((d, i) => (
                        <p key={i} className="text-xs text-slate-500">{d}</p>
                      ))}
                    </div>
                    <div className="qr mt-3 flex justify-center">
                      <QRCodeSVG value={labelContent.qrValue} size={80} level="H" />
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-slate-400">
                    <QrCode className="w-16 h-16 mx-auto mb-3" />
                    <p>Select an entity to preview label</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* iter37 — Template Preview Dialog */}
      <Dialog open={!!previewTpl} onOpenChange={(o) => !o && setPreviewTpl(null)}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="template-preview-title">Preview · {previewTpl?.template_name}</DialogTitle>
          </DialogHeader>
          {previewTpl && (
            <div className="space-y-3">
              <div className="text-xs text-slate-500 flex items-center gap-3 flex-wrap">
                <span><span className="font-semibold text-slate-700">Type:</span> {previewTpl.label_type}</span>
                <span className="mono">{previewTpl.width_mm}mm × {previewTpl.height_mm}mm</span>
                <span>{(previewTpl.fields || []).length} field(s)</span>
              </div>
              <div className="border border-slate-200 rounded-sm p-3 bg-slate-50" data-testid="template-preview-canvas-wrap">
                {(previewTpl.fields || []).length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-12">No fields defined for this template yet.</p>
                ) : (
                  <LabelCanvas template={previewTpl} onChange={() => {}} />
                )}
              </div>
              <div className="text-[11px] text-slate-500 italic">
                Linked fields render as <span className="mono">{'{field_name}'}</span> placeholders. Final values are resolved at print time using the selected entity. To test with real data, open the <span className="font-semibold">Print Labels</span> tab or print from the entity's page.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LabelsPage;
